import { getPoranaytteetByTarkastuskohdeV2, getTestitV2, getTestiCaptionV2 } from '../../../api/api';

// PDF renderer for the new v2-only "Rappausnäyte" testi type (no v1 equivalent
// to match styling against — built directly from the reference "NÄYTETIEDOT"
// table the user supplied). All min/max(/mediaani) values here are printed
// verbatim as typed — direct entry, no computation, same as the live view.

const TYYPPI = 'Rappaus';

const TH = { font: 'Lato', fontSize: 8, bold: true, color: '#fff', fillColor: '#04aa00', alignment: 'center' };
const TD = (text, opts = {}) => ({ text: text ?? '—', font: 'Lato', fontSize: 8, alignment: 'center', ...opts });

const layout = {
  hLineWidth: () => 0.5, vLineWidth: () => 0.5,
  hLineColor: () => '#adb5bd', vLineColor: () => '#adb5bd',
  paddingLeft: () => 4, paddingRight: () => 4,
  paddingTop: () => 4, paddingBottom: () => 4,
  fillColor: (row) => row === 0 ? '#04aa00' : (row % 2 === 0 ? '#f8f9fa' : null),
};

const headerRow = [
  { text: 'Näyte:', ...TH },
  { text: 'Kokonaispaksuus\nmin-max:\n[mm]', ...TH },
  { text: 'Pintalaasti\nmin-max/mediaani:\n[mm]', ...TH },
  { text: 'Verkotuslaasti\nmin-max/mediaani:\n[mm]', ...TH },
  { text: 'Verkon sijainti\nverkotuslaastin ulkopinnasta: [mm]', ...TH },
];

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
const romanFor = (n) => ROMAN[n - 1] ?? String(n);

function fmtN(v) {
  if (v === null || v === undefined || v === '') return null;
  return String(v).replace('.', ',');
}
function fmtMinMax(min, max) {
  const a = fmtN(min), b = fmtN(max);
  if (a == null && b == null) return null;
  return `${a ?? '—'} - ${b ?? '—'}`;
}
function fmtMinMaxMed(min, max, med) {
  const range = fmtMinMax(min, max);
  if (range == null) return null;
  const m = fmtN(med);
  return m != null ? `${range} / ${m}` : range;
}

// Returns pdfmake text runs (not a plain string) so the "I kerros"/"II
// kerros" label can be bolded independently of its min-max/mediaani value —
// a plain joined string has no way to style just part of the text.
function fmtVerkotuslaasti(layers) {
  const list = (layers ?? []).filter(l => fmtMinMaxMed(l.min, l.max, l.mediaani) != null);
  if (!list.length) return '—';
  const showLabel = list.length > 1;
  const runs = [];
  list.forEach((l, i) => {
    if (i > 0) runs.push({ text: '\n' });
    if (showLabel) runs.push({ text: `${romanFor(l.kerros ?? i + 1)} kerros `, bold: true });
    runs.push({ text: fmtMinMaxMed(l.min, l.max, l.mediaani) });
  });
  return runs;
}

export async function renderRappausnayteBlockV2({ block, content, tableCounter }) {
  const nav = block?.data?.nav ?? {};
  if (!nav.tkId) return;

  let samples;
  try { samples = (await getPoranaytteetByTarkastuskohdeV2(nav.tkId)).data ?? []; }
  catch (e) { console.warn('[RenderRappausnayteBlockV2] failed to fetch poränäytteet:', e); return; }

  const eligible = samples.filter(s => (s.testi || '').toLowerCase().includes(TYYPPI.toLowerCase()));
  if (!eligible.length) return;

  const rows = (await Promise.all(eligible.map(async s => {
    let testit;
    try { testit = (await getTestitV2(s.id)).data ?? []; } catch { return null; }
    const t = testit.find(x => x.tyyppi === TYYPPI);
    if (!t) return null;
    return { tunnus: s.nayte_tunnus, ...t.arvo };
  }))).filter(Boolean);
  if (!rows.length) return;

  const tblNum = tableCounter ? ++tableCounter.value : null;
  const bodyRows = rows.map(row => [
    TD(row.tunnus),
    TD(fmtMinMax(row.kokonaispaksuusMin, row.kokonaispaksuusMax)),
    TD(fmtMinMaxMed(row.pintalaastiMin, row.pintalaastiMax, row.pintalaastiMediaani)),
    TD(fmtVerkotuslaasti(row.verkotuslaasti)),
    TD(row.verkonSijainti || '—'),
  ]);

  // testi_caption is one per (tarkastuskohde, tyyppi), shared by every
  // sample row in this table — see testi_caption on the backend. Rich-text
  // captions (TaulukkoFooter) are persisted as JSON.stringify({value, html});
  // a bare string (pre-rich-text data) passes through unchanged.
  const readCap = (raw) => {
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'value' in parsed) return (parsed.value ?? '').trim();
    } catch { /* plain string */ }
    return String(raw).trim();
  };
  let captionRow;
  try { captionRow = (await getTestiCaptionV2(nav.tkId, TYYPPI)).data; } catch { /* fall back below */ }
  // No fallback — an empty caption stays empty in the PDF. The table
  // number still always shows.
  const captionText = readCap(captionRow?.caption);

  content.push({
    stack: [
      { table: { headerRows: 1, widths: ['14%', '18%', '20%', '24%', '24%'], body: [headerRow, ...bodyRows] }, layout, margin: [0, 0, 0, 0] },
      ...(tblNum !== null ? [{
        text: captionText
          ? [{ text: `Taulukko ${tblNum}. `, bold: true }, { text: captionText, bold: false }]
          : [{ text: `Taulukko ${tblNum}.`, bold: true }],
        font: 'Lato', fontSize: 9,
        margin: [0, 4, 0, 0],
      }] : []),
    ],
    margin: [0, 0, 0, 15],
  });
}
